import React from 'react';
import typography from '../../utils/typography';
import { SerializedStyles, css } from '@emotion/core';
import { useTheme } from '@veneer/theme';
import getPillBorder from '../../utils/get_pill_border';

type StylesType = {
  root: {
    base: SerializedStyles;
    selected: {
      base: SerializedStyles;
      multiple: {
        base: SerializedStyles;
        hc: SerializedStyles;
      };
      hc: SerializedStyles;
    };
    hc: SerializedStyles;
    disabled: SerializedStyles;
    pill: SerializedStyles;
  };
  label: {
    base: SerializedStyles;
    span: SerializedStyles;
  };
  icons: {
    base: SerializedStyles;
    selected: {
      base: SerializedStyles;
      iconCheckmark: SerializedStyles;
      disabled: SerializedStyles;
    };
  };
};

const useStyles = (): StylesType => {
  const theme = useTheme();
  return React.useMemo(
    () => ({
      root: {
        base: css({
          alignItems: 'center',
          border: '1px solid transparent',
          borderRadius: theme.borderRadius3,
          boxSizing: 'border-box',
          color: theme.color.foreground.default,
          cursor: 'pointer',
          display: 'flex',
          fontFamily: theme.typography.family0,
          fontSize: theme.typography.size2,
          lineHeight: theme.typography.lineHeight3,
          padding: `6px ${theme.layout.size2}`,
          pointerEvents: 'auto',
          position: 'relative',
          textDecoration: 'none',
          transition: 'background-color 100ms linear, color 100ms linear',
          userSelect: 'none',

          '&:hover': {
            backgroundColor: theme.color.neutral.background.hover,
            color: theme.color.foreground.default,
          },

          '&:active': {
            backgroundColor: theme.color.neutral.background.active,
            color: theme.color.foreground.default,
          },

          '&:not(:last-of-type)': {
            marginBottom: theme.layout.size1,
          },

          '&[data-focus="true"]': {
            borderColor: theme.color.primary.base.default,
            color: theme.color.foreground.default,
          },

          '&__iconbutton': {
            color: 'inherit',
            height: theme.layout.size4,
            marginRight: theme.layout.size2,
            width: theme.layout.size4,
          },

          '&.icon': {
            '.vn-option__iconbutton': {
              marginRight: `calc(${theme.layout.size1} / 2)`,
            },
          },

          '.vn-option__icon': {
            color: 'inherit',
            marginRight: theme.layout.size2,
          },
        }),
        selected: {
          base: css({
            backgroundColor: theme.color.primary.base.default,
            color: theme.color.primary.foreground.default,

            '&:hover': {
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.hover,
            },
            '&:active': {
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.default,
            },

            '&[data-focus="true"]': {
              backgroundColor: theme.color.primary.base.hover,
              color: theme.color.primary.foreground.default,

              '> .icon_checkmark': {
                display: 'none',
              },

              '> .icon_x': {
                display: 'block',
              },
            },
          }),
          multiple: {
            base: css({
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.default,
              '&:hover': {
                backgroundColor: theme.color.primary.base.hover,
                color: theme.color.primary.foreground.default,

                '> .icon_x': {
                  display: 'block',
                },
                '> .icon_checkmark': {
                  display: 'none',
                },
              },

              '&:active': {
                backgroundColor: theme.color.primary.base.active,
                color: theme.color.primary.foreground.default,

                '> .icon_x': {
                  display: 'block',
                },
                '> .icon_checkmark': {
                  display: 'none',
                },
              },
            }),
            hc: css({
              '&:hover': {
                backgroundColor: theme.color.primary.base.hover,
                borderColor: theme.color.primary.base.hover,
                color: theme.color.primary.foreground.default,
              },

              '&:active': {
                backgroundColor: theme.color.primary.base.active,
                borderColor: theme.color.primary.base.active,
                color: theme.color.primary.foreground.default,
              },
            }),
          },

          hc: css({
            backgroundColor: theme.color.primary.base.default,
            color: theme.color.primary.foreground.default,

            // revisit this on a11y work
            '&[data-focus="true"]': {
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.default,
              borderColor: theme.color.primary.base.default,

              '> .icon_checkmark': {
                display: 'none',
              },

              '> .icon_x': {
                display: 'block',
              },
            },

            '&:hover, &:active': {
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.default,
              borderColor: theme.color.primary.base.default,
            },
          }),
        },

        disabled: css({
          backgroundColor: 'transparent',
          color: theme.color.foreground.disabled,
          pointerEvents: 'none',
        }),

        hc: css({
          color: theme.color.primary.base.default,
          '&:hover': {
            backgroundColor: theme.color.primary.base.hover,
            color: theme.color.primary.foreground.default,
          },

          '&[data-focus="true"]': {
            backgroundColor: theme.color.primary.base.hover,
            borderColor: theme.color.primary.base.hover,
            color: theme.color.primary.foreground.default,
          },

          '&:active': {
            backgroundColor: theme.color.primary.base.active,
            borderColor: theme.color.primary.base.active,
            color: theme.color.primary.foreground.default,
          },
        }),

        pill: css({
          borderRadius: getPillBorder,
        }),
      },
      icons: {
        base: css({
          marginRight: theme.layout.size2,
          display: 'none',
          color: 'inherit',
          height: theme.layout.size4,
          width: theme.layout.size4,
        }),
        selected: {
          base: css({
            fill: theme.color.primary.foreground.default,
          }),
          iconCheckmark: css({
            display: 'block',
          }),
          disabled: css({
            fill: theme.color.foreground.disabled,
          }),
        },
      },
      label: {
        base: css({
          display: 'inline-flex',
          alignItems: 'center',
          ...typography(theme).label,
          minHeight: theme.layout.size6,
          overflow: 'hidden',
        }),
        span: css({
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
      },
    }),
    [theme]
  );
};

export default useStyles;
